import React from "react";
import {Link, useParams} from "react-router-dom";
import {useTranslation} from "@unibuddy/intl";
import {Alert, Box, Button, Heading, Icon, Stack, Text, Tooltip, Badge} from "@unibuddy/patron";
import {IconExclamationCircle} from "@unibuddy/icons";
import styled from "styled-components";
import {useQuery} from "react-apollo";

import {ChatHeaderUI} from "shared/Chat/components/ChatHeaderUI/ChatHeaderUI";
import {rootURL} from "shared/Utils/WhereAmI";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import useLocalStorage from "shared/functions/useLocalStorage";
import {MentorAvatar} from "shared/Mentor/components/MentorAvatar/MentorAvatar";
import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import {
    BuddyField,
    GetChatGroupByIdQueryQuery,
    GetChatGroupByIdQueryQueryVariables,
    PublicMentorField,
} from "src/types/generated";

import {ChatBackButton} from "../../components/ChatBackButton/ChatBackButton";
import {getMentorInboxInfoOne} from "../../utils/InboxInfo";
import s from "./ChatHeader.pcss";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_BAD_VIBES,
    INTL_CHAT_RULES,
    INTL_CONTACT_SUPPORT,
    INTL_GIVING_TIPS,
    INTL_GOT_IT,
    INTL_HAPPY_CHATTING,
    INTL_OFFENSIVE_MESSAGES,
    INTL_ROMANTIC_COMMENTS,
    INTL_SHARING_EXPERIENCE,
    INTL_SHARING_SOCIAL_MEDIA,
    INTL_START_A_CHAT,
    INTL_TALKING_ABOUT_COURSE,
    INTL_THINGS_WE_DONT_LIKE,
    INTL_THINGS_WE_LIKE,
} from "../../../../../translations/codeOfConduct";
import {INTL_MENTOR_PROFILE} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import buddiesProfileQuery from "../../../../../widgets/modules/Buddies/queries/buddiesProfileQuery";
import MentorProfileQuery from "../../../../../shared/Mentor/queries/MentorProfileQuery";
import {useChat} from "../../providers/ChatProvider";
import {LoadingAvatar, LoadingTitle} from "../LoadingChat/LoadingChat";
import {GetChatGroupIdQuery} from "../../queries/getChatGroupByIdQuery";
import {INTL_GENERIC_ERROR} from "../../../../../../src/translations/errorMessage";
import {useUniversity} from "../../../../modules/UniversityProvider/UniversityProvider";

const StyledBox = styled(Box)`
    display: ruby;
`;

const CodeOfConductText = ({t}) => (
    <Stack space="medium">
        <Text>
            {t(
                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_START_A_CHAT.key}`,
                INTL_START_A_CHAT.defaultValue,
            )}
        </Text>
        <Stack space="small">
            <Heading level="2" size="xxsmall">
                ✅{" "}
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_THINGS_WE_LIKE.key}`,
                    INTL_THINGS_WE_LIKE.defaultValue,
                )}
            </Heading>
            <ul>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_SHARING_EXPERIENCE.key}`,
                                INTL_SHARING_EXPERIENCE.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_TALKING_ABOUT_COURSE.key}`,
                                INTL_TALKING_ABOUT_COURSE.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_GIVING_TIPS.key}`,
                                INTL_GIVING_TIPS.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
            </ul>
            <Heading level="2" size="xxsmall">
                ⛔{" "}
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_THINGS_WE_DONT_LIKE.key}`,
                    INTL_THINGS_WE_DONT_LIKE.defaultValue,
                )}
            </Heading>
            <ul>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_OFFENSIVE_MESSAGES.key}`,
                                INTL_OFFENSIVE_MESSAGES.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_SHARING_SOCIAL_MEDIA.key}`,
                                INTL_SHARING_SOCIAL_MEDIA.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_BAD_VIBES.key}`,
                                INTL_BAD_VIBES.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_ROMANTIC_COMMENTS.key}`,
                                INTL_ROMANTIC_COMMENTS.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
            </ul>
            <Text>
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CONTACT_SUPPORT.key}`,
                    INTL_CONTACT_SUPPORT.defaultValue,
                )}
                <ThemedLink
                    external
                    to="mailto:support@unibuddy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    support@unibuddy.com
                </ThemedLink>
            </Text>
        </Stack>
        <Text>
            {t(
                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_HAPPY_CHATTING.key}`,
                INTL_HAPPY_CHATTING.defaultValue,
            )}
        </Text>
    </Stack>
);

type ChatHeaderProps = {
    chatRulesOverride: boolean;
};

type OpponentType = {
    id?: string;
    firstName?: string;
    accountRole?: string;
    inboxInfoOne?: string;
    inboxInfoTwo?: string;
    isEnterprise?: boolean;
};

export function ChatHeader({chatRulesOverride}: ChatHeaderProps) {
    const {t} = useTranslation([
        IntlNamespaces.CHAT,
        IntlNamespaces.CODE_OF_CONDUCT,
        IntlNamespaces.ERROR_MESSAGE,
    ]);
    const {
        events: {itemClicked},
        itemNames: {chatRules},
    } = useAnalytics();
    const {opponentId, conversationId, chatGroupId} = useParams<{
        opponentId?: string;
        conversationId?: string;
        chatGroupId?: string;
    }>();

    const [rulesOpen, setRulesOpen] = React.useState(false);
    const [chatRulesSeen, setChatRulesSeen] = useLocalStorage("chatRulesSeen", chatRulesOverride);
    const {chatGroupId: legacyChatGroupId} = useChat();
    const university = useUniversity();
    const isDegreeTypesEnabled = !!university?.features.get("enableDegreeTypes");

    const toggleRulesOpen = () => {
        if (!rulesOpen) {
            itemClicked({
                prefix: chatRules,
            });
            setChatRulesSeen(true);
        }
        setRulesOpen(!rulesOpen);
    };

    const {
        data: buddyData,
        loading: buddyLoading,
        error: buddyError,
    } = useQuery<{buddy: BuddyField}>(buddiesProfileQuery, {
        variables: {
            id: opponentId,
            isBlogsEnabled: false,
        },
        skip: !opponentId,
    });

    const {
        data: mentorData,
        loading: mentorLoading,
        error: mentorError,
    } = useQuery<{publicMentor: PublicMentorField}>(MentorProfileQuery, {
        variables: {
            id: opponentId,
            isDegreeTypesEnabled,
            isBlogsEnabled: false,
        },
        skip: !opponentId,
    });

    const {
        data: chatGroupData,
        loading: chatGroupLoading,
        error: chatGroupError,
    } = useQuery<GetChatGroupByIdQueryQuery, GetChatGroupByIdQueryQueryVariables>(
        GetChatGroupIdQuery,
        {
            variables: {chatGroupId: chatGroupId || legacyChatGroupId},
            skip: !chatGroupId && !legacyChatGroupId,
        },
    );

    if (chatGroupLoading || mentorLoading || buddyLoading) {
        return (
            <ChatHeaderUI leftAddon={<ChatBackButton />} rightAddon={<div />}>
                {LoadingAvatar}
                <ChatHeaderUI.Title>{LoadingTitle}</ChatHeaderUI.Title>
            </ChatHeaderUI>
        );
    }

    let errorOccurred = false;
    if (
        (opponentId && !buddyData && !mentorData) ||
        ((conversationId || chatGroupId) && !chatGroupData) ||
        chatGroupError ||
        (mentorError && buddyError)
    ) {
        errorOccurred = true;
    }

    const resolvedChatGroup = chatGroupData?.chatGroupById ?? {};
    let opponent: OpponentType;
    let buddyRole: string;

    // This is being done to ensure that we have a consistent opponent object
    // since now we are getting the data from different field types(AnyChatUserField | PublicMentorField | BuddyField)
    // from earlier AnyChatUserField & AnyChatUserField. Some fields were not present in the BuddyField & PublicMentorField
    if (resolvedChatGroup?.opponent) {
        opponent = resolvedChatGroup.opponent;
        buddyRole = `${opponent.accountRole === "staff" ? "staff" : "students"}`;
    } else if (mentorData?.publicMentor) {
        opponent = {
            ...mentorData.publicMentor,
            inboxInfoTwo: mentorData?.publicMentor?.university?.name,
            isEnterprise: mentorData?.publicMentor?.university?.isEnterprise,
        };
        opponent.inboxInfoOne = getMentorInboxInfoOne({sender: opponent, university, t});
        buddyRole = `${opponent.accountRole === "staff" ? "staff" : "students"}`;
    } else if (buddyData?.buddy) {
        opponent = {
            ...buddyData.buddy,
            inboxInfoOne: buddyData?.buddy?.staffFields?.department,
            inboxInfoTwo: buddyData?.buddy?.university?.name,
            isEnterprise: buddyData?.buddy?.university?.isEnterprise,
        };
        buddyRole = "staff";
    }

    const rootUrl: string = rootURL(location.pathname);
    const getBuddyPosition = () => (location.search ? location.search : "?buddyPosition=chat");

    const mentorLink = `${rootUrl}/buddies/${buddyRole}/${
        opponentId ?? opponent?.id
    }${getBuddyPosition()}`;
    const mentorsName: string = opponent?.firstName;

    return (
        <>
            <ChatHeaderUI
                leftAddon={<ChatBackButton />}
                rightAddon={
                    <Button
                        size="sm"
                        round
                        iconOnly
                        aria-label={t(
                            `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CHAT_RULES.key}`,
                            INTL_CHAT_RULES.defaultValue,
                        )}
                        onClick={toggleRulesOpen}
                    >
                        <Icon icon={IconExclamationCircle} color={chatRulesSeen ? "grey" : "red"} />
                    </Button>
                }
            >
                {errorOccurred && (
                    <Text>
                        {t(
                            `${IntlNamespaces.ERROR_MESSAGE}:${INTL_GENERIC_ERROR.key}`,
                            INTL_GENERIC_ERROR.defaultValue,
                        )}
                    </Text>
                )}
                {opponent && !errorOccurred && (
                    <React.Fragment>
                        <ChatHeaderUI.Title>
                            <div>
                                <Link
                                    className={s.mentorProfileLink}
                                    to={mentorLink}
                                    aria-label={t(
                                        `${IntlNamespaces.CHAT}:${INTL_MENTOR_PROFILE.key}`,
                                        isFunction(INTL_MENTOR_PROFILE.defaultValue)
                                            ? INTL_MENTOR_PROFILE.defaultValue(mentorsName)
                                            : INTL_MENTOR_PROFILE.defaultValue,
                                        {
                                            name: mentorsName,
                                        },
                                    )}
                                >
                                    <MentorAvatar
                                        data-test-id="chat-header-mentor-avatar"
                                        mentor={opponent}
                                    />
                                    <div className={s.mentorInfo}>
                                        {mentorsName} <br />
                                        {opponent.inboxInfoOne &&
                                        opponent.inboxInfoOne.length > 48 ? (
                                            <Tooltip label={opponent.inboxInfoOne}>
                                                <small className={s.cursorDefault}>
                                                    {opponent.inboxInfoOne}
                                                </small>
                                            </Tooltip>
                                        ) : (
                                            <small className={s.cursorDefault}>
                                                {opponent.inboxInfoOne}
                                            </small>
                                        )}
                                        <br />
                                        {opponent?.isEnterprise &&
                                            (opponent.inboxInfoTwo &&
                                            opponent.inboxInfoTwo.length > 48 ? (
                                                <div>
                                                    <Tooltip label={opponent.inboxInfoTwo}>
                                                        <small className={s.cursorDefault}>
                                                            <strong>{opponent.inboxInfoTwo}</strong>
                                                        </small>
                                                    </Tooltip>
                                                </div>
                                            ) : (
                                                <small className={s.cursorDefault}>
                                                    <strong>{opponent.inboxInfoTwo}</strong>
                                                </small>
                                            ))}
                                    </div>
                                </Link>
                            </div>
                        </ChatHeaderUI.Title>
                        {resolvedChatGroup?.creationSource &&
                            resolvedChatGroup?.creationSource.type === "marketplace" && (
                                <Badge
                                    className={s.sourceBadge}
                                    color="violet"
                                    size="standard"
                                    elevation={0}
                                >
                                    {resolvedChatGroup?.creationSource.name}
                                </Badge>
                            )}
                    </React.Fragment>
                )}
            </ChatHeaderUI>
            <Alert
                title={t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CHAT_RULES.key}`,
                    INTL_CHAT_RULES.defaultValue,
                )}
                onDismiss={toggleRulesOpen}
                isOpen={rulesOpen}
                size="xsmall"
                dismissText={t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_GOT_IT.key}`,
                    INTL_GOT_IT.defaultValue,
                )}
                type="light"
                titleClassname={s.codeOfConductTitle}
                buttonsClassname={s.codeOfConductButtons}
            >
                <CodeOfConductText t={t} />
            </Alert>
        </>
    );
}
